import { create } from 'zustand';
import { SavedProject, ProjectListItem } from '../types/project';
import { saveProjectToFile, loadProjectFromFile } from '../utils/fileManager';

interface ProjectStore {
  // 当前项目
  currentProjectId: string | null;
  
  // 保存状态
  lastSaveTime: number | null;
  lastAutoSaveTime: number | null;
  isSaving: boolean;
  saveMessage: string | null;
  
  // 获取所有项目列表
  getProjects: () => ProjectListItem[];
  
  // 获取单个项目完整数据
  getProject: (id: string) => SavedProject | null;
  
  // 创建新项目
  createProject: (name: string, description?: string) => string;
  
  // 更新项目
  updateProject: (id: string, updates: Partial<SavedProject>) => void;
  
  // 删除项目
  deleteProject: (id: string) => void;
  
  // 保存当前项目数据
  saveCurrentProject: (projectData: any, isAuto?: boolean) => void;
  
  // 保存项目到加密文件
  saveProjectToEncryptedFile: (projectData: any, projectName: string) => Promise<void>;
  
  // 从加密文件加载项目
  loadProjectFromEncryptedFile: (file: File) => Promise<SavedProject>;
  
  // 设置当前项目ID
  setCurrentProjectId: (id: string | null) => void;
  
  // 显示保存消息
  showSaveMessage: (message: string) => void;
  
  // 清除保存消息
  clearSaveMessage: () => void;
}

const STORAGE_KEY = 'kimap_projects';

// 从localStorage读取项目列表
const loadProjects = (): SavedProject[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    }
  } catch (error) {
    console.error('Failed to load projects:', error);
  }
  return [];
};

// 保存项目列表到localStorage
const saveProjects = (projects: SavedProject[]) => {
  try {
    const dataStr = JSON.stringify(projects);
    const dataSizeMB = new Blob([dataStr]).size / 1024 / 1024;
    
    // 检查数据大小，如果超过5MB，警告用户
    if (dataSizeMB > 5) {
      console.warn(`⚠️ localStorage数据过大: ${dataSizeMB.toFixed(2)}MB`);
      console.warn('建议导出项目文件并清理旧项目');
    }
    
    localStorage.setItem(STORAGE_KEY, dataStr);
  } catch (error) {
    console.error('Failed to save projects:', error);
    
    // 如果是QuotaExceededError，提示用户清理
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      alert('浏览器存储空间已满！请导出项目文件并删除一些旧项目。');
    }
  }
};

export const useProjectStore = create<ProjectStore>((set, get) => ({
  currentProjectId: null,
  lastSaveTime: null,
  lastAutoSaveTime: null,
  isSaving: false,
  saveMessage: null,

  getProjects: () => {
    const projects = loadProjects();
    return projects.map(p => ({
      id: p.id,
      name: p.name,
      description: p.description,
      thumbnail: p.thumbnail,
      createdAt: p.createdAt,
      updatedAt: p.updatedAt,
      autoSavedAt: p.autoSavedAt,
    }));
  },

  getProject: (id: string) => {
    const projects = loadProjects();
    return projects.find(p => p.id === id) || null;
  },

  createProject: (name: string, description?: string) => {
    const projects = loadProjects();
    const id = `project_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const now = Date.now();
    
    const newProject: SavedProject = {
      id,
      name,
      description,
      createdAt: now,
      updatedAt: now,
      data: null,
    };
    
    projects.push(newProject);
    saveProjects(projects);
    
    return id;
  },

  updateProject: (id: string, updates: Partial<SavedProject>) => {
    const projects = loadProjects();
    const index = projects.findIndex(p => p.id === id);
    
    if (index !== -1) {
      projects[index] = {
        ...projects[index],
        ...updates,
        updatedAt: Date.now(),
      };
      saveProjects(projects);
    }
  },

  deleteProject: (id: string) => {
    const projects = loadProjects();
    const filtered = projects.filter(p => p.id !== id);
    saveProjects(filtered);
    
    // 如果删除的是当前项目，清除当前项目ID
    if (get().currentProjectId === id) {
      set({ currentProjectId: null });
    }
  },

  saveCurrentProject: (projectData: any, isAuto: boolean = false) => {
    const { currentProjectId } = get();
    if (!currentProjectId) return;
    
    set({ isSaving: true });
    
    try {
      const projects = loadProjects();
      const index = projects.findIndex(p => p.id === currentProjectId);
      
      if (index !== -1) {
        const now = Date.now();
        projects[index] = {
          ...projects[index],
          data: projectData,
          updatedAt: now,
          ...(isAuto && { autoSavedAt: now }),
        };
        saveProjects(projects);
        
        const time = new Date(now).toLocaleTimeString('zh-CN', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        });
        
        if (isAuto) {
          set({ 
            lastAutoSaveTime: now,
            isSaving: false,
          });
          get().showSaveMessage(`${time} 自动保存成功`);
        } else {
          set({ 
            lastSaveTime: now,
            isSaving: false,
          });
          get().showSaveMessage(`${time} 保存成功`);
        }
      } else {
        set({ isSaving: false });
      }
    } catch (error) {
      console.error('Failed to save project:', error);
      set({ isSaving: false });
      get().showSaveMessage('保存失败');
    }
  },

  saveProjectToEncryptedFile: async (projectData: any, projectName: string) => {
    set({ isSaving: true });
    
    try {
      await saveProjectToFile(projectData, projectName);
      
      const time = new Date().toLocaleTimeString('zh-CN', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });
      
      set({ 
        lastSaveTime: Date.now(),
        isSaving: false,
      });
      get().showSaveMessage(`${time} 项目已保存为加密文件`);
    } catch (error) {
      console.error('Failed to save encrypted file:', error);
      set({ isSaving: false });
      throw error;
    }
  },

  loadProjectFromEncryptedFile: async (file: File) => {
    try {
      const projectData = await loadProjectFromFile(file);
      return projectData;
    } catch (error) {
      console.error('Failed to load encrypted file:', error);
      throw error;
    }
  },

  setCurrentProjectId: (id: string | null) => {
    set({ currentProjectId: id });
  },

  showSaveMessage: (message: string) => {
    set({ saveMessage: message });
    
    // 3秒后自动清除消息
    setTimeout(() => {
      get().clearSaveMessage();
    }, 3000);
  },

  clearSaveMessage: () => {
    set({ saveMessage: null });
  },
}));
